import { CatalogRepository } from '@lib/api/repository/catalog/catalog.repository';
import {
  AdminCardDetails,
  AdminCollectionDetails,
  CardDetails,
  CardSelection,
  CatalogFilters,
  CatalogPage,
  CatalogPreview,
  CatalogSynchronization,
  Collection,
  CollectionCatalogPage,
  CollectionInput,
  Game,
  LandingCatalogSection,
  Rarity,
} from '@lib/types';

import { Requester } from '../clients';

import {
  AdminCollectionDetailsDTO,
  CardDetailsDTO,
  CardSelectionDTO,
  CatalogPageDTO,
  CatalogPreviewDTO,
  CollectionCatalogPageDTO,
  LandingCatalogSectionDTO,
  createCardDetails,
  createCardSelection,
  createCatalogPage,
  createCatalogPreview,
  createCollectionCatalogPage,
  createLandingCatalogSection,
} from './models';

interface CatalogSelectionPageDTO {
  items: CardSelectionDTO[];
}

interface CatalogPreviewResponseDTO {
  items: CatalogPreviewDTO[];
}

interface LandingCatalogDTO {
  sections: LandingCatalogSectionDTO[];
}

const toParams = (
  filters?: CatalogFilters & { cursor?: string; page?: number },
) => (filters ? { ...filters } : undefined);

export default class CatalogAPI implements CatalogRepository {
  async adminCollectionDetails(
    id: string,
    signal?: AbortSignal,
  ): Promise<AdminCollectionDetails> {
    const { data } = await Requester.get<AdminCollectionDetailsDTO>(
      `/marketplace/admin/catalog/collections/${encodeURIComponent(id)}`,
      { signal },
    );

    return {
      id: data.id,
      gameId: data.gameId,
      name: data.name,
      code: data.code,
      preReleaseDate: data.preReleaseDate,
      releaseDate: data.releaseDate,
      cardCount: data.cardCount,
      numOfCards: data.numOfCards,
      providerId: data.providerId,
      marketplaceVisible: data.marketplaceVisible,
      game: { ...data.game },
      printings: data.printings.map((printing) => ({
        id: printing.id,
        code: printing.code,
        active: printing.active,
        isAlternateArt: printing.isAlternateArt,
        sortOrder: printing.sortOrder,
        collection: { ...printing.collection },
        rarity: { ...printing.rarity },
        artwork: printing.artwork,
        card: {
          id: printing.card.id,
          nameEn: printing.card.nameEn,
          namePt: printing.card.namePt,
        },
      })),
    };
  }

  async landingCatalog(signal?: AbortSignal): Promise<LandingCatalogSection[]> {
    const response = (
      await Requester.get<LandingCatalogDTO>('/marketplace/landing/catalog', {
        signal,
      })
    ).data;

    return response.sections.map(createLandingCatalogSection);
  }

  async latestSynchronization(
    signal?: AbortSignal,
  ): Promise<CatalogSynchronization | null> {
    return (
      await Requester.get<CatalogSynchronization | null>(
        '/marketplace/catalog/sync/status',
        { signal },
      )
    ).data;
  }

  async allCollectionCards(
    collectionId: string,
    signal?: AbortSignal,
  ): Promise<CollectionCatalogPage> {
    const response = (
      await Requester.get<CollectionCatalogPageDTO>(
        `/marketplace/catalog/collections/${collectionId}/cards/all`,
        { signal },
      )
    ).data;

    return createCollectionCatalogPage(response);
  }

  async collectionCards(
    collectionId: string,
    cursor?: string,
    signal?: AbortSignal,
    page?: number,
  ): Promise<CollectionCatalogPage> {
    const response = (
      await Requester.get<CollectionCatalogPageDTO>(
        `/marketplace/catalog/collections/${collectionId}/cards`,
        { signal, params: { cursor, page } },
      )
    ).data;

    return createCollectionCatalogPage(response);
  }

  async preview(
    search: string,
    gameCode?: string,
    signal?: AbortSignal,
  ): Promise<CatalogPreview[]> {
    const response = (
      await Requester.get<CatalogPreviewResponseDTO>(
        '/marketplace/catalog/preview',
        { signal, params: { search, gameCode } },
      )
    ).data;

    return response.items.map(createCatalogPreview);
  }

  async games(signal?: AbortSignal): Promise<Game[]> {
    return (
      await Requester.get<Game[]>('/marketplace/catalog/games', { signal })
    ).data;
  }

  async collections(
    gameCode: string,
    signal?: AbortSignal,
  ): Promise<Collection[]> {
    return (
      await Requester.get<Collection[]>('/marketplace/catalog/collections', {
        signal,
        params: { gameCode },
      })
    ).data;
  }

  async adminCollections(
    gameCode: string,
    signal?: AbortSignal,
  ): Promise<Collection[]> {
    return (
      await Requester.get<Collection[]>(
        `/marketplace/admin/catalog/games/${encodeURIComponent(gameCode)}/collections`,
        { signal },
      )
    ).data;
  }

  async rarities(signal?: AbortSignal): Promise<Rarity[]> {
    return (
      await Requester.get<Rarity[]>('/marketplace/catalog/rarities', { signal })
    ).data;
  }

  async languages(
    signal?: AbortSignal,
  ): Promise<{ id: string; name: string; code: string }[]> {
    return (
      await Requester.get<{ id: string; name: string; code: string }[]>(
        '/marketplace/catalog/languages',
        { signal },
      )
    ).data;
  }

  async details(
    gameCode: string,
    cardSlug: string,
    signal?: AbortSignal,
  ): Promise<CardDetails> {
    const response = (
      await Requester.get<CardDetailsDTO>(
        `/marketplace/catalog/games/${gameCode}/cards/${cardSlug}`,
        { signal },
      )
    ).data;

    return createCardDetails(response);
  }

  async options(cardId: string, signal?: AbortSignal): Promise<CardSelection> {
    const response = (
      await Requester.get<CardSelectionDTO>(
        `/marketplace/catalog/${cardId}/options`,
        { signal },
      )
    ).data;

    return createCardSelection(response);
  }

  async list(
    filters?: CatalogFilters & { cursor?: string; page?: number },
    signal?: AbortSignal,
  ): Promise<CatalogPage> {
    const response = (
      await Requester.get<CatalogPageDTO>('/marketplace/catalog', {
        signal,
        params: toParams(filters),
      })
    ).data;

    return createCatalogPage(response);
  }

  async adminCards(
    filters?: CatalogFilters & { cursor?: string; page?: number },
    signal?: AbortSignal,
  ): Promise<CatalogPage> {
    const response = (
      await Requester.get<CatalogPageDTO>('/marketplace/admin/catalog/cards', {
        signal,
        params: toParams(filters),
      })
    ).data;

    return createCatalogPage(response);
  }

  async search(
    filters: CatalogFilters,
    signal?: AbortSignal,
  ): Promise<CardSelection[]> {
    return (
      await Requester.get<CatalogSelectionPageDTO>(
        '/marketplace/catalog/search',
        { signal, params: toParams(filters) },
      )
    ).data.items.map(createCardSelection);
  }

  async synchronize() {
    return (
      await Requester.post<{ cards: number; printings: number }>(
        '/marketplace/catalog/sync/ygoprodeck',
      )
    ).data;
  }

  async listRarities(
    signal?: AbortSignal,
  ): Promise<import('@lib/types').Rarity[]> {
    return (
      await Requester.get<import('@lib/types').Rarity[]>(
        '/marketplace/admin/catalog/rarities',
        { signal },
      )
    ).data;
  }

  async createRarity(data: {
    name: string;
    shortName?: string | null;
    code: string;
  }): Promise<import('@lib/types').Rarity> {
    return (
      await Requester.post<import('@lib/types').Rarity>(
        '/marketplace/admin/catalog/rarities',
        { body: data },
      )
    ).data;
  }

  async updateRarity(
    id: string,
    data: { name: string; shortName?: string | null; code: string },
  ): Promise<import('@lib/types').Rarity> {
    return (
      await Requester.put<import('@lib/types').Rarity>(
        `/marketplace/admin/catalog/rarities/${id}`,
        { body: data },
      )
    ).data;
  }

  async deleteRarity(id: string): Promise<void> {
    await Requester.delete(`/marketplace/admin/catalog/rarities/${id}`);
  }

  async listCollections(
    signal?: AbortSignal,
  ): Promise<import('@lib/types').Collection[]> {
    return (
      await Requester.get<import('@lib/types').Collection[]>(
        '/marketplace/admin/catalog/collections',
        { signal },
      )
    ).data;
  }

  async createCollection(data: CollectionInput): Promise<Collection> {
    return (
      await Requester.post<import('@lib/types').Collection>(
        '/marketplace/admin/catalog/collections',
        {
          body: {
            game_id: data.gameId,
            name: data.name,
            code: data.code,
            pre_release_date: data.preReleaseDate,
            release_date: data.releaseDate,
          },
        },
      )
    ).data;
  }

  async updateCollection(
    id: string,
    data: Partial<CollectionInput>,
  ): Promise<Collection> {
    return (
      await Requester.put<import('@lib/types').Collection>(
        `/marketplace/admin/catalog/collections/${id}`,
        {
          body: {
            game_id: data.gameId,
            name: data.name,
            code: data.code,
            pre_release_date: data.preReleaseDate,
            release_date: data.releaseDate,
          },
        },
      )
    ).data;
  }

  async deleteCollection(id: string): Promise<void> {
    await Requester.delete(`/marketplace/admin/catalog/collections/${id}`);
  }

  async createCard(
    data: import('@lib/types').CreateCardInput,
  ): Promise<import('@lib/types').CatalogCard> {
    let body: unknown = data;

    if (data.file) {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('name_en', data.nameEn);

      if (data.namePt) {
        formData.append('name_pt', data.namePt);
      }

      if (data.descriptionEn) {
        formData.append('description_en', data.descriptionEn);
      }

      if (data.descriptionPt) {
        formData.append('description_pt', data.descriptionPt);
      }

      if (data.gameCode) {
        formData.append('game_code', data.gameCode);
      }

      if (data.collectionId) {
        formData.append('collection_id', data.collectionId);
      }

      if (data.rarityId) {
        formData.append('rarity_id', data.rarityId);
      }

      if (data.printingCode) {
        formData.append('printing_code', data.printingCode);
      }

      if (data.yugiohDetails) {
        formData.append(
          'yugioh_details[frame_type]',
          data.yugiohDetails.frameType,
        );

        if (data.yugiohDetails.category) {
          formData.append(
            'yugioh_details[category]',
            data.yugiohDetails.category,
          );
        }

        if (data.yugiohDetails.attack !== undefined) {
          formData.append(
            'yugioh_details[attack]',
            String(data.yugiohDetails.attack),
          );
        }

        if (data.yugiohDetails.defense !== undefined) {
          formData.append(
            'yugioh_details[defense]',
            String(data.yugiohDetails.defense),
          );
        }

        if (data.yugiohDetails.level !== undefined) {
          formData.append(
            'yugioh_details[level]',
            String(data.yugiohDetails.level),
          );
        }

        if (data.yugiohDetails.race) {
          formData.append('yugioh_details[race]', data.yugiohDetails.race);
        }

        if (data.yugiohDetails.attribute) {
          formData.append(
            'yugioh_details[attribute]',
            data.yugiohDetails.attribute,
          );
        }

        if (data.yugiohDetails.archetype) {
          formData.append(
            'yugioh_details[archetype]',
            data.yugiohDetails.archetype,
          );
        }

        if (data.yugiohDetails.typeLine?.length) {
          data.yugiohDetails.typeLine.forEach((t, i) => {
            formData.append(`yugioh_details[type_line][${i}]`, t);
          });
        }
      }

      body = formData;
    }

    return (
      await Requester.post<import('@lib/types').CatalogCard>(
        '/marketplace/admin/catalog/cards',
        { body },
      )
    ).data;
  }

  async adminCard(
    cardId: string,
    signal?: AbortSignal,
  ): Promise<AdminCardDetails> {
    return (
      await Requester.get<AdminCardDetails>(
        `/marketplace/admin/catalog/cards/${cardId}`,
        { signal },
      )
    ).data;
  }

  async updateCard(
    cardId: string,
    data: import('@lib/types').UpdateCardInput,
  ): Promise<AdminCardDetails> {
    return (
      await Requester.put<AdminCardDetails>(
        `/marketplace/admin/catalog/cards/${cardId}`,
        { body: data },
      )
    ).data;
  }

  async deleteCard(cardId: string): Promise<void> {
    await Requester.delete(`/marketplace/admin/catalog/cards/${cardId}`);
  }
}
